import React, { useEffect, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { useDispatch, useSelector } from 'react-redux';
import {
  FaUpload,
  FaDownload,
  FaCheckCircle,
  FaTimesCircle,
  FaSpinner,
  FaUsers,
  FaFileExcel,
  FaRedo,
} from 'react-icons/fa';
import Wrapper from '../../components/Wrapper';
import { fetchPartners } from '../../store/features/partnersSlice';
import { fetchFieldOfficersByAdminId } from '../../store/features/fieldOfficerByPartnerSlice';
import axiosInstance from '../../utils/axios';
import { getRole } from '../../utils/role';
import { fetchFieldOfficerInfo } from '../../store/features/fieldOfficersSlice';

const REQUIRED_COLUMNS = ['first_name', 'last_name', 'mobile', 'address', 'village', 'pincode', 'city', 'state'];
const OPTIONAL_COLUMNS = ['email'];
/** Excel header order (email before address — matches backend column names by key) */
const TEMPLATE_COLUMNS = [
  'first_name',
  'last_name',
  'mobile',
  'email',
  'address',
  'village',
  'pincode',
  'city',
  'state',
];
const MAX_ROWS = 500;

/* ── Validate a single parsed row, return array of error strings ── */
function validateRow(row) {
  const errors = [];
  if (!String(row.first_name || '').trim()) errors.push('first_name required');
  if (!String(row.last_name || '').trim()) errors.push('last_name required');
  if (!/^\d{10}$/.test(String(row.mobile || '').trim())) errors.push('mobile must be 10 digits');
  if (!String(row.address || '').trim()) errors.push('address required');
  if (!String(row.village || '').trim()) errors.push('village required');
  if (!/^\d{6}$/.test(String(row.pincode || '').trim())) errors.push('pincode must be 6 digits');
  if (!String(row.city || '').trim()) errors.push('city required');
  if (!String(row.state || '').trim()) errors.push('state required');
  return errors;
}

const BulkOnboardFarmers = () => {
  const dispatch = useDispatch();
  
  const role = getRole();
  const isSupport = role === 'SUPPORT';
  const isPartner = (role === 'PARTNER' || role === 'TEAM_MEMBER');
  const isFieldOfficer = role === 'FIELD_OFFICER';

  const { partners, status: partnersStatus } = useSelector((s) => s.partners);
  const { fieldOfficers, status: foStatus } = useSelector((s) => s.fieldOfficerByPartner || {});
  
  const { partner } = useSelector((s) => s.partner);
  const { fieldOfficerInfo } = useSelector((s) => s.fieldOfficer);

  const [selectedPartnerId, setSelectedPartnerId] = useState('');
  const [selectedFoId, setSelectedFoId] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [submitStatus, setSubmitStatus] = useState('idle');
  const [results, setResults] = useState(null);
  const [globalError, setGlobalError] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isSupport && partnersStatus === 'idle') {
      dispatch(fetchPartners({ page: 1, limit: 200 }));
    }
    
    if (isPartner && partner?.admin_id) {
      dispatch(fetchFieldOfficersByAdminId({ admin_id: partner.admin_id }));
    }

    if (isFieldOfficer && !fieldOfficerInfo) {
      console.log("Dispatching fetchFieldOfficerInfo...");
      dispatch(fetchFieldOfficerInfo());
    }

  }, [dispatch, partnersStatus, isSupport, isPartner, partner, isFieldOfficer, fieldOfficerInfo]);

  /* ── Partner change ── */
  const handlePartnerChange = (e) => {
    const id = e.target.value;
    setSelectedPartnerId(id);
    setSelectedFoId('');
    setGlobalError('');
    if (id) dispatch(fetchFieldOfficersByAdminId({ admin_id: id }));
  };

  /* ── File pick & client-side parse ── */
  const handleFileChange = (e) => {
    const picked = e.target.files[0];
    if (!picked) return;
    setFile(picked);
    setResults(null);
    setGlobalError('');

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const workbook = XLSX.read(evt.target.result, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });

        if (rows.length === 0) {
          setGlobalError('The uploaded file has no data rows.');
          setPreview([]);
          return;
        }
        if (rows.length > MAX_ROWS) {
          setGlobalError(`Maximum ${MAX_ROWS} rows allowed. Found ${rows.length}.`);
          setPreview([]);
          return;
        }

        const parsed = rows.map((row, i) => ({
          rowNum: i + 2,
          ...row,
          rowErrors: validateRow(row),
        }));
        setPreview(parsed);
      } catch {
        setGlobalError('Could not parse the file. Please use the provided template.');
        setPreview([]);
      }
    };
    reader.readAsArrayBuffer(picked);
  };

  /* ── Download blank template ── */
  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      TEMPLATE_COLUMNS,
      ['Ramesh', 'Kumar', '9876543210', 'optionalemail@x.com', 'Near Bus Stand, Main Road', 'Rampur', '110001', 'New Delhi', 'Delhi'],
    ]);
    ws['!cols'] = TEMPLATE_COLUMNS.map(() => ({ wch: 22 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Farmers');
    XLSX.writeFile(wb, 'bulk_farmer_template.xlsx');
  };

  /* ── Download failed rows ── */
  const downloadFailed = () => {
    if (!results) return;
    const failed = results.results.filter((r) => r.status === 'error');
    const previewMap = Object.fromEntries(preview.map((p) => [p.rowNum, p]));
    const rows = failed.map((r) => {
      const orig = previewMap[r.row] || {};
      return {
        first_name: orig.first_name || '',
        last_name: orig.last_name || '',
        mobile: orig.mobile || '',
        email: orig.email || '',
        address: orig.address || '',
        village: orig.village || '',
        pincode: orig.pincode || '',
        city: orig.city || '',
        state: orig.state || '',
        error: r.message,
      };
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = Object.keys(rows[0] || {}).map(() => ({ wch: 22 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Failed');
    XLSX.writeFile(wb, 'bulk_farmer_failed.xlsx');
  };

  let hasRequiredIds = false;
  if (isSupport) {
    hasRequiredIds = Boolean(selectedPartnerId && selectedFoId);
  } else if (isPartner) {
    hasRequiredIds = Boolean(selectedFoId);
  } else if (isFieldOfficer) {
    hasRequiredIds = Boolean(fieldOfficerInfo?.field_officer_id);
  }

  const hasClientErrors = preview.some((r) => r.rowErrors.length > 0);
  const canSubmit = hasRequiredIds && file && preview.length > 0 && !hasClientErrors;

  /* ── Submit ── */
  const handleSubmit = async () => {
    if (!canSubmit) return;

    setSubmitStatus('loading');
    setGlobalError('');

    try {
      let submitAdminId, submitFoId;
      
      if (isSupport) {
        submitAdminId = selectedPartnerId;
        submitFoId = selectedFoId;
      } else if (isPartner) {
        submitAdminId = partner?.admin_id;
        submitFoId = selectedFoId;
      } else if (isFieldOfficer) {
        // Leave submitAdminId undefined; backend will fetch it
        submitFoId = fieldOfficerInfo?.field_officer_id;
      }

      if (!submitFoId) {
        setGlobalError('Missing Field Officer configuration. Cannot process upload.');
        setSubmitStatus('idle');
        return;
      }

      const formData = new FormData();
      if (submitAdminId) {
        formData.append('admin_id', submitAdminId);
      }
      formData.append('field_officer_id', submitFoId);
      formData.append('file', file);

      const { data } = await axiosInstance.post('/super-admin/bulk-add-users', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      setResults(data);
      setSubmitStatus('done');
    } catch (err) {
      setGlobalError(err.response?.data?.msg || 'Upload failed. Please try again.');
      setSubmitStatus('idle');
    }
  };

  /* ── Reset ── */
  const handleReset = () => {
    setSelectedPartnerId('');
    setSelectedFoId('');
    setFile(null);
    setPreview([]);
    setResults(null);
    setSubmitStatus('idle');
    setGlobalError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <Wrapper noScroll>
      <div className="max-w-5xl mx-auto w-full">
        {/* Header */}
        <div className="p-4 lg:p-5 border-b border-slate-100">
          <div className="flex items-start gap-3">
            <div className="p-1.5 bg-emerald-50 rounded-lg text-emerald-600">
              <FaUsers className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-slate-900">Bulk Onboard Farmers</h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Upload an Excel file to register multiple farmers at once.
              </p>
            </div>
          </div>
        </div>

        {/* Step 1: Select Partner & Field Officer */}
        {(isSupport || isPartner) && (
          <div className="p-4 lg:p-5 border-b border-slate-100">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
               Step 1 — Select {isSupport ? 'Partner & ' : ''}Field Officer
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              
              {isSupport && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Partner <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={selectedPartnerId}
                    onChange={handlePartnerChange}
                    disabled={submitStatus === 'loading'}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    <option value="">
                      {partnersStatus === 'loading' ? 'Loading partners…' : 'Select Partner'}
                    </option>
                    {partners.map((p) => (
                      <option key={p.admin_id} value={p.admin_id}>
                        {p.entity_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Field Officer Dropdown */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Field Officer <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedFoId}
                  onChange={(e) => setSelectedFoId(e.target.value)}
                  disabled={(isSupport && !selectedPartnerId) || foStatus === 'loading' || submitStatus === 'loading'}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:bg-slate-50 disabled:text-slate-400"
                >
                  <option value="">
                    {isSupport && !selectedPartnerId
                      ? 'Select Partner first'
                      : foStatus === 'loading'
                      ? 'Loading field officers…'
                      : 'Select Field Officer'}
                  </option>
                  {foStatus === 'succeeded' &&
                    fieldOfficers.map((fo) => (
                      <option key={fo.field_officer_id} value={fo.field_officer_id}>
                        {fo.name}
                      </option>
                    ))}
                </select>
                {foStatus === 'succeeded' && ((isSupport && selectedPartnerId) || isPartner) && fieldOfficers.length === 0 && (
                  <p className="text-xs text-slate-500 mt-1">No field officers found.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Step 2: Template + Upload ── */}
        <div className="p-4 lg:p-5 border-b border-slate-100">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
            {isFieldOfficer ? 'Step 1' : 'Step 2'} — Download Template &amp; Upload File
          </p>

          {/* Column guide */}
          <div className="mb-4 bg-slate-50 rounded-lg p-3 border border-slate-200">
            <p className="text-xs font-semibold text-slate-700 mb-2">Required columns in your Excel:</p>
            <div className="flex flex-wrap gap-1.5">
              {REQUIRED_COLUMNS.map((col) => (
                <span
                  key={col}
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-50 text-red-700 border border-red-200"
                >
                  {col} *
                </span>
              ))}
              {OPTIONAL_COLUMNS.map((col) => (
                <span
                  key={col}
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200"
                >
                  {col}
                </span>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-2">
              <span className="text-red-600 font-medium">*</span> required &nbsp;·&nbsp;
              <span className="text-slate-500">unlabelled = optional</span>
            </p>
          </div>

          <div className="flex flex-wrap gap-3 items-center">
            <button
              type="button"
              onClick={downloadTemplate}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-white border border-emerald-400 text-emerald-700 hover:bg-emerald-50 transition-colors"
            >
              <FaDownload className="w-3.5 h-3.5" />
              Download Template
            </button>

            <label className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors cursor-pointer">
              <FaFileExcel className="w-3.5 h-3.5 text-green-600" />
              {file ? file.name : 'Choose Excel File (.xlsx)'}
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                className="hidden"
                onChange={handleFileChange}
                disabled={submitStatus === 'loading'}
              />
            </label>
          </div>

          {globalError && (
            <p className="mt-3 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 border border-red-200">
              {globalError}
            </p>
          )}
        </div>

        {/* ── Step 3: Preview table ── */}
        {preview.length > 0 && submitStatus !== 'done' && (
          <div className="p-4 lg:p-5 border-b border-slate-100">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                {isFieldOfficer ? 'Step 2' : 'Step 3'} — Preview ({preview.length} rows)
                {hasClientErrors && (
                  <span className="ml-2 text-red-600 normal-case font-normal">
                    — fix errors before submitting
                  </span>
                )}
              </p>
            </div>
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold whitespace-nowrap">Row</th>
                    <th className="px-3 py-2 text-left font-semibold whitespace-nowrap">First Name</th>
                    <th className="px-3 py-2 text-left font-semibold whitespace-nowrap">Last Name</th>
                    <th className="px-3 py-2 text-left font-semibold whitespace-nowrap">Mobile</th>
                    <th className="px-3 py-2 text-left font-semibold whitespace-nowrap">Address</th>
                    <th className="px-3 py-2 text-left font-semibold whitespace-nowrap">Village</th>
                    <th className="px-3 py-2 text-left font-semibold whitespace-nowrap">Pincode</th>
                    <th className="px-3 py-2 text-left font-semibold whitespace-nowrap">City</th>
                    <th className="px-3 py-2 text-left font-semibold whitespace-nowrap">State</th>
                    <th className="px-3 py-2 text-left font-semibold whitespace-nowrap">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {preview.map((row) => (
                    <tr
                      key={row.rowNum}
                      className={row.rowErrors.length > 0 ? 'bg-red-50' : ''}
                    >
                      <td className="px-3 py-1.5 text-slate-400">{row.rowNum}</td>
                      <td className="px-3 py-1.5">{row.first_name}</td>
                      <td className="px-3 py-1.5">{row.last_name}</td>
                      <td className="px-3 py-1.5">{row.mobile}</td>
                      <td className="px-3 py-1.5 max-w-[140px] truncate" title={row.address}>{row.address}</td>
                      <td className="px-3 py-1.5">{row.village}</td>
                      <td className="px-3 py-1.5">{row.pincode}</td>
                      <td className="px-3 py-1.5">{row.city}</td>
                      <td className="px-3 py-1.5">{row.state}</td>
                      <td className="px-3 py-1.5">
                        {row.rowErrors.length === 0 ? (
                          <span className="text-emerald-600 font-medium">OK</span>
                        ) : (
                          <span className="text-red-600" title={row.rowErrors.join('; ')}>
                            {row.rowErrors.length} error(s)
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Results table ── */}
        {results && submitStatus === 'done' && (
          <div className="p-4 lg:p-5 border-b border-slate-100">
            {/* Summary */}
            <div className="flex flex-wrap gap-3 mb-4">
              <div className="flex items-center gap-2 px-4 py-2 bg-emerald-50 rounded-lg border border-emerald-200">
                <FaCheckCircle className="text-emerald-600 w-4 h-4" />
                <span className="text-sm font-semibold text-emerald-700">
                  {results.successCount} created
                </span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-red-50 rounded-lg border border-red-200">
                <FaTimesCircle className="text-red-500 w-4 h-4" />
                <span className="text-sm font-semibold text-red-600">
                  {results.errorCount} failed
                </span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 rounded-lg border border-slate-200">
                <span className="text-sm text-slate-600">
                  {results.total} total rows
                </span>
              </div>
            </div>

            {/* Result rows */}
            <div className="overflow-x-auto rounded-lg border border-slate-200 mb-4">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Row</th>
                    <th className="px-3 py-2 text-left font-semibold">Name</th>
                    <th className="px-3 py-2 text-left font-semibold">Status</th>
                    <th className="px-3 py-2 text-left font-semibold">Message</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {results.results.map((r) => (
                    <tr key={r.row} className={r.status === 'error' ? 'bg-red-50' : ''}>
                      <td className="px-3 py-1.5 text-slate-400">{r.row}</td>
                      <td className="px-3 py-1.5 font-medium text-slate-800">{r.name}</td>
                      <td className="px-3 py-1.5">
                        {r.status === 'success' ? (
                          <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold">
                            <FaCheckCircle className="w-3 h-3" /> Success
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-red-600 font-semibold">
                            <FaTimesCircle className="w-3 h-3" /> Failed
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-slate-600">{r.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {results.errorCount > 0 && (
              <button
                type="button"
                onClick={downloadFailed}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-white border border-red-300 text-red-600 hover:bg-red-50 transition-colors"
              >
                <FaDownload className="w-3.5 h-3.5" />
                Download Failed Rows
              </button>
            )}
          </div>
        )}

        {/* ── Footer ── */}
        <div className="bg-slate-50 px-4 py-3 flex items-center justify-between border-t border-slate-100 rounded-b-xl">
          <button
            type="button"
            onClick={handleReset}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <FaRedo className="w-3 h-3" />
            Start Over
          </button>

          {submitStatus !== 'done' && (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit || submitStatus === 'loading'}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-medium text-white shadow-sm transition-all duration-200 ${
                !canSubmit || submitStatus === 'loading'
                  ? 'bg-emerald-300 cursor-not-allowed'
                  : 'bg-emerald-600 hover:bg-emerald-700'
              }`}
            >
              {submitStatus === 'loading' ? (
                <>
                  <FaSpinner className="w-4 h-4 animate-spin" />
                  Uploading…
                </>
              ) : (
                <>
                  <FaUpload className="w-4 h-4" />
                  Submit Upload
                </>
              )}
            </button>
          )}
        </div>

      </div>
    </Wrapper>
  );
};

export default BulkOnboardFarmers;